/**
 * Theme Definitions
 * 
 * 10 predefined themes with light and dark mode variants.
 * All colors use OKLCH format for perceptual uniformity.
 */

import { Theme } from './types'

/**
 * Helper to create theme colors with consistent structure
 * Exported for use in theme lab and custom theme generation
 */
export function createThemeColors(
  hue: number,
  lightAccent: string,
  darkAccent: string
): { light: Theme['light']; dark: Theme['dark']; preview: Theme['preview'] } {
  return {
    light: {
      accent: lightAccent,
      accentForeground: 'oklch(0.9846 0.0017 247.8389)',
      accentSoft: `oklch(0.92 0.06 ${hue})`,
      accentSoftForeground: 'oklch(0.2101 0.0318 264.6645)',
      ring: `oklch(0.71 0.10 ${hue})`,
      chart1: lightAccent,
      sidebarAccent: lightAccent,
      sidebarAccentForeground: 'oklch(0.9846 0.0017 247.8389)',
      sidebarAccentSoft: `oklch(0.92 0.06 ${hue})`,
      sidebarAccentSoftForeground: 'oklch(0.2101 0.0318 264.6645)',
      sidebarRing: `oklch(0.71 0.10 ${hue})`,
    },
    dark: {
      accent: darkAccent,
      accentForeground: 'oklch(0.2101 0.0318 264.6645)',
      accentSoft: `oklch(0.55 0.12 ${hue})`,
      accentSoftForeground: 'oklch(0.9846 0.0017 247.8389)',
      ring: `oklch(0.55 0.10 ${hue})`,
      chart1: darkAccent,
      sidebarAccent: darkAccent,
      sidebarAccentForeground: 'oklch(0.9846 0.0017 247.8389)',
      sidebarAccentSoft: `oklch(0.55 0.12 ${hue})`,
      sidebarAccentSoftForeground: 'oklch(0.9846 0.0017 247.8389)',
      sidebarRing: `oklch(0.55 0.10 ${hue})`,
    },
    preview: {
      accent: lightAccent,
      secondary: `oklch(0.9683 0.0069 ${hue})`,
      accentSoft: `oklch(0.92 0.06 ${hue})`,
      background: 'oklch(0.9846 0.0017 247.8389)',
    },
  }
}

// Default Theme (Blue-based, existing design)
const defaultThemeColors = createThemeColors(247.5537, 'oklch(0.5381 0.1433 247.5537)', 'oklch(0.6231 0.1880 259.8145)')

// Blue Theme
const blueThemeColors = createThemeColors(240, 'oklch(0.5381 0.1433 240)', 'oklch(0.6231 0.1880 240)')

// Green Theme
const greenThemeColors = createThemeColors(150, 'oklch(0.5381 0.1433 150)', 'oklch(0.6231 0.1880 150)')

// Purple Theme
const purpleThemeColors = createThemeColors(280, 'oklch(0.5381 0.1433 280)', 'oklch(0.6231 0.1880 280)')

// Orange Theme
const orangeThemeColors = createThemeColors(60, 'oklch(0.5381 0.1433 60)', 'oklch(0.6231 0.1880 60)')

// Red Theme
const redThemeColors = createThemeColors(25, 'oklch(0.5381 0.1433 25)', 'oklch(0.6231 0.1880 25)')

// Teal Theme
const tealThemeColors = createThemeColors(180, 'oklch(0.5381 0.1433 180)', 'oklch(0.6231 0.1880 180)')

// Indigo Theme
const indigoThemeColors = createThemeColors(260, 'oklch(0.5381 0.1433 260)', 'oklch(0.6231 0.1880 260)')

// Pink Theme
const pinkThemeColors = createThemeColors(330, 'oklch(0.5381 0.1433 330)', 'oklch(0.6231 0.1880 330)')

// Gray Theme (neutral, low chroma) — 11 theme-specific keys only; chroma bumped per contract
const grayThemeColors = {
  light: {
    accent: 'oklch(0.4500 0.0234 264.3637)',
    accentForeground: 'oklch(0.9846 0.0017 247.8389)',
    accentSoft: 'oklch(0.9288 0.0126 255.5078)',
    accentSoftForeground: 'oklch(0.2101 0.0318 264.6645)',
    ring: 'oklch(0.71 0.05 261.32)',
    chart1: 'oklch(0.4500 0.0234 264.3637)',
    sidebarAccent: 'oklch(0.4500 0.0234 264.3637)',
    sidebarAccentForeground: 'oklch(0.9846 0.0017 247.8389)',
    sidebarAccentSoft: 'oklch(0.9288 0.0126 255.5078)',
    sidebarAccentSoftForeground: 'oklch(0.2101 0.0318 264.6645)',
    sidebarRing: 'oklch(0.71 0.05 261.32)',
  },
  dark: {
    accent: 'oklch(0.7500 0.035 261.3246)',
    accentForeground: 'oklch(0.2101 0.0318 264.6645)',
    accentSoft: 'oklch(0.45 0.04 256.80)',
    accentSoftForeground: 'oklch(0.9846 0.0017 247.8389)',
    ring: 'oklch(0.55 0.05 264.36)',
    chart1: 'oklch(0.7500 0.035 261.3246)',
    sidebarAccent: 'oklch(0.7500 0.035 261.3246)',
    sidebarAccentForeground: 'oklch(0.2101 0.0318 264.6645)',
    sidebarAccentSoft: 'oklch(0.45 0.04 256.80)',
    sidebarAccentSoftForeground: 'oklch(0.9846 0.0017 247.8389)',
    sidebarRing: 'oklch(0.55 0.05 264.36)',
  },
  preview: {
    accent: 'oklch(0.4500 0.0234 264.3637)',
    secondary: 'oklch(0.9683 0.0069 247.8956)',
    accentSoft: 'oklch(0.9288 0.0126 255.5078)',
    background: 'oklch(0.9846 0.0017 247.8389)',
  },
}

export const themes: Theme[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'The default blue theme',
    ...defaultThemeColors,
  },
  {
    id: 'blue',
    name: 'Blue',
    description: 'Ocean blue theme',
    ...blueThemeColors,
  },
  {
    id: 'green',
    name: 'Green',
    description: 'Fresh green theme',
    ...greenThemeColors,
  },
  {
    id: 'purple',
    name: 'Purple',
    description: 'Royal purple theme',
    ...purpleThemeColors,
  },
  {
    id: 'orange',
    name: 'Orange',
    description: 'Vibrant orange theme',
    ...orangeThemeColors,
  },
  {
    id: 'red',
    name: 'Red',
    description: 'Bold red theme',
    ...redThemeColors,
  },
  {
    id: 'teal',
    name: 'Teal',
    description: 'Calm teal theme',
    ...tealThemeColors,
  },
  {
    id: 'indigo',
    name: 'Indigo',
    description: 'Deep indigo theme',
    ...indigoThemeColors,
  },
  {
    id: 'pink',
    name: 'Pink',
    description: 'Soft pink theme',
    ...pinkThemeColors,
  },
  {
    id: 'gray',
    name: 'Gray',
    description: 'Neutral gray theme',
    ...grayThemeColors,
  },
]

/**
 * Get theme by ID
 */
export function getThemeById(id: string): Theme | undefined {
  return themes.find(theme => theme.id === id)
}

/**
 * Get default theme
 */
export function getDefaultTheme(): Theme {
  return themes[0]
}
